import { Channel } from '@/Enums/Channel.enum';
import Color from '@/Enums/Color.enum';
import { IControlOption } from '@/Interfaces/ControlOption.interface';
import PackagesSection from '@/components/PackagesSection';
import SearchList from '@/components/SearchList';
import { useAppSelector } from '@/store/hooks';
import { selectSharedApiDealGroupControlOptions, selectSharedApiPackages } from '@/store/selectors/sharedApi.selectors';
import { List } from '@mui/icons-material';
import { FormLabel, Paper } from '@mui/material';
import { useCallback, useId, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface IProps {
  currentChannel: Channel;
}

const DealGroupsSection = ({ currentChannel }: IProps) => {
  const id = useId();
  const { control, setValue } = useFormContext();

  const controlName = useMemo(() => `${currentChannel}.dealGroups`, [currentChannel]);
  const packageControlName = useMemo(() => `${currentChannel}.packageId`, [currentChannel]);

  const value = useWatch({
    control,
    name: controlName,
    defaultValue: [],
  });

  const { dealGroups: options, loading } = useAppSelector(selectSharedApiDealGroupControlOptions);
  const { packages } = useAppSelector(selectSharedApiPackages);
  const hasPackages = useMemo<boolean>(() => !!packages?.length, [packages?.length]);

  //#region useCallback
  const setFormValue = useCallback(
    (values: IControlOption[]) => {
      setValue(controlName, values, { shouldDirty: true, shouldValidate: true });
    },
    [setValue, controlName],
  );
  //#endregion

  if (hasPackages) {
    return (
      <Paper sx={{ padding: '16px' }}>
        <PackagesSection fieldName={packageControlName} id={id} />
      </Paper>
    );
  }

  if (!options.length) {
    return null;
  }

  return (
    <Paper sx={{ padding: '16px' }}>
      <FormLabel
        htmlFor={id}
        sx={{ marginBottom: '12px', fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center' }}
      >
        <List sx={{ marginRight: '8px' }} />
        <span>Deal Groups</span>
      </FormLabel>
      <SearchList
        id={id}
        sx={{
          borderWidth: '1px',
          borderRadius: '8px',
          borderColor: Color.GreyBorder,
          maxHeight: '300px',
          minHeight: '100px',
        }}
        label='Deal Groups'
        onChange={setFormValue}
        options={options}
        value={value}
        disabled={loading}
      />
    </Paper>
  );
};

export default DealGroupsSection;
